import {
    FilterConfig,
    SupportedHazardFilter,
    Image,
    getLangObjects,
    LanguageCode,
    colors,
} from '@hazadapt-git/public-core-base'
import { Typography } from '@mui/material'
import classNames from 'classnames'
import React, { FC } from 'react'
import { useStyles } from '../../lib/styles/universal'
import { useWindowSizeUp } from '../../lib/utils'
import {
    ChipGrid,
    PrepCheckCardBaseProps,
    PrepCheckLimitReachedModal,
    PrepCheckWelcomeModal,
} from '../molecules'
import {
    LanguageSelectorModal,
    PrepCheckCardGrid,
    PrepCheckLandingBanner,
} from '../organisms'
import { makeStyles } from 'tss-react/mui'
import { IoOptions } from 'react-icons/io5'

interface PrepCheckLandingPageTemplateProps {
    journeyPhase: string
    journeyPhaseIcon: Image
    journeyPhaseSubcopy: string
    prepChecksCompleted: number
    totalPrepChecks: number
    filters: FilterConfig[]
    activeFilters: SupportedHazardFilter[]
    onChipPress(name: string): void
    onChipReset: React.MouseEventHandler
    prepChecks: PrepCheckCardBaseProps[]
    onSharePress?(e: React.MouseEvent, id: number): void
    onPrepCheckClick(id: number): void
    onJourneyCompletionClick: React.MouseEventHandler
    onMyEmergencyItemsClick: React.MouseEventHandler
    loggedIn: boolean
    showPrepCheckWelcome?: boolean
    onPrepCheckWelcomeClose: React.MouseEventHandler
    loading?: boolean
    onTranslateClick?: React.MouseEventHandler
    languages: LanguageCode[]
    selectedLanguage: LanguageCode
    languageSelectorModalOpen?: boolean
    onLanguageSelectorModalClose: React.MouseEventHandler
    onLanguageChange(lang: LanguageCode): void
    filtersExpanded: boolean
    onFilterViewToggle(open: boolean): void
    // Guest-related props
    showPrepCheckLimitWarning: boolean
    onPrepCheckLimitWarningModalClose: () => void
    onSignUpClick: () => void
}

export const PrepCheckLandingPageTemplate: FC<
    PrepCheckLandingPageTemplateProps
> = (props: PrepCheckLandingPageTemplateProps) => {
    const { classes } = useStyles()
    const { classes: localClasses } = useLocalStyles()
    const mediumWindowOrLarger = useWindowSizeUp('md')

    return (
        <div>
            <PrepCheckLandingBanner
                journeyPhaseIcon={props.journeyPhaseIcon}
                journeyPhase={props.journeyPhase}
                journeyPhaseSubcopy={props.journeyPhaseSubcopy}
                prepChecksCompleted={props.prepChecksCompleted}
                totalPrepChecks={props.totalPrepChecks}
                onJourneyCompletionClick={props.onJourneyCompletionClick}
                onMyEmergencyItemsClick={props.onMyEmergencyItemsClick}
                loggedIn={props.loggedIn}
                loading={props.loading}
                onTranslateClick={props.onTranslateClick}
                onSignUpClick={props.onSignUpClick}
            />
            <div
                className={classNames(
                    classes.page,
                    classes.pageWithNoTopPadding
                )}
                style={{
                    padding: '0 1rem',
                    marginBottom: '3rem',
                }}
            >
                {!props.loading ? (
                    <>
                        {props.filters.length > 0 && (
                            <div
                                style={{
                                    alignSelf: mediumWindowOrLarger
                                        ? 'center'
                                        : 'flex-start',
                                    display:
                                        props.filters.length > 0
                                            ? 'block'
                                            : 'none',
                                    padding: `${
                                        props.filtersExpanded ||
                                        props.activeFilters.length > 0
                                            ? '0.5rem'
                                            : '1rem'
                                    } 0 1.5rem`,
                                }}
                            >
                                <ChipGrid
                                    chips={props.filters}
                                    activeChips={props.activeFilters}
                                    availableChips={props.filters.map(
                                        (f) => f.key
                                    )}
                                    guidingText={
                                        mediumWindowOrLarger ? (
                                            'Filter By:'
                                        ) : (
                                            <button
                                                className={
                                                    localClasses.mwFilterBy
                                                }
                                                onClick={() =>
                                                    props.onFilterViewToggle(
                                                        !props.filtersExpanded
                                                    )
                                                }
                                            >
                                                <IoOptions
                                                    size="1.5rem"
                                                    color={
                                                        props.filtersExpanded
                                                            ? colors.grays.NOIR
                                                            : colors.secondary
                                                                  .BONDI
                                                    }
                                                />
                                                <Typography
                                                    color={
                                                        props.filtersExpanded
                                                            ? colors.grays.NOIR
                                                            : colors.secondary
                                                                  .BONDI
                                                    }
                                                    fontWeight={500}
                                                    whiteSpace="nowrap"
                                                    px="0.5rem"
                                                >
                                                    {props.filtersExpanded
                                                        ? `Hide Filters`
                                                        : 'Show Filters'}
                                                </Typography>
                                            </button>
                                        )
                                    }
                                    onChipPress={props.onChipPress}
                                    onResetPress={props.onChipReset}
                                    showDeleteIcon
                                    collapsible={!mediumWindowOrLarger}
                                    collapsed={!props.filtersExpanded}
                                />
                            </div>
                        )}
                    </>
                ) : null}
                <PrepCheckCardGrid
                    data={props.prepChecks}
                    onPrepCheckClick={props.onPrepCheckClick}
                    onSharePress={props.onSharePress}
                    loading={props.loading}
                />
            </div>

            {/* Language selector modal */}
            <LanguageSelectorModal
                languages={getLangObjects(props.languages)}
                selectedLanguage={props.selectedLanguage}
                open={!!props.languageSelectorModalOpen}
                onClose={props.onLanguageSelectorModalClose}
                onLanguagePress={props.onLanguageChange}
            />

            {/* Welcome modal */}
            <PrepCheckWelcomeModal
                open={!!props.showPrepCheckWelcome}
                onClose={props.onPrepCheckWelcomeClose}
                onSignUpClick={props.onSignUpClick}
                lang={props.selectedLanguage}
            />

            {/* Guest Prep Check limit reached modal */}
            <PrepCheckLimitReachedModal
                open={props.showPrepCheckLimitWarning}
                onClose={props.onPrepCheckLimitWarningModalClose}
                onSignUpClick={props.onSignUpClick}
                lang={props.selectedLanguage}
            />
        </div>
    )
}

const useLocalStyles = makeStyles()({
    mwFilterBy: {
        all: 'unset',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
    },
})
